// ============================================================================
// clientsStorage — CRUD de clientes (Supabase REST)
// ============================================================================
// Substitui o getClients/upsertClient/deleteClient do v2 que usava localStorage.
// Padrão alinhado com financeStorage.js: REST direto na tabela, sem cache local.
// ============================================================================

// Acesso via PROXY autenticado (chave de serviço no servidor) em vez da chave
// pública direta — ver secureRest.js. A sintaxe PostgREST continua idêntica.
import { SUPABASE_URL, restHeaders, restFetchAll } from './secureRest.js';

// ─── Conversão snake_case (DB) ↔ camelCase (app) ────────────────────────────
// Mantém compat com a interface v2 (birthDate, totalVisits, etc).

function toApp(row) {
  if (!row) return null;
  return {
    id: row.id,
    name: row.name || '',
    phone: row.phone || '',
    email: row.email || '',
    cpf: row.cpf || '',
    birthDate: row.birth_date || '',
    gender: row.gender || '',
    address: row.address || '',
    notes: row.notes || '',
    unitId: row.unit_id || '',
    lastVisit: row.last_visit || null,
    totalVisits: row.total_visits || 0,
    totalSpent: Number(row.total_spent) || 0,
    prontuario: row.prontuario || [],
    anamnese: row.anamnese || {},
    historico: row.historico || [],
    createdAt: row.created_at,
    updatedAt: row.updated_at,
  };
}

function toDb(client) {
  const row = {};
  if (client.name !== undefined) row.name = client.name;
  if (client.phone !== undefined) row.phone = client.phone || null;
  if (client.email !== undefined) row.email = client.email || null;
  if (client.cpf !== undefined) row.cpf = client.cpf || null;
  if (client.birthDate !== undefined) row.birth_date = client.birthDate || null;
  if (client.gender !== undefined) row.gender = client.gender || null;
  if (client.address !== undefined) row.address = client.address || null;
  if (client.notes !== undefined) row.notes = client.notes || null;
  if (client.unitId !== undefined) row.unit_id = client.unitId || null;
  if (client.lastVisit !== undefined) row.last_visit = client.lastVisit || null;
  if (client.totalVisits !== undefined) row.total_visits = Number(client.totalVisits) || 0;
  if (client.totalSpent !== undefined) row.total_spent = Number(client.totalSpent) || 0;
  if (client.prontuario !== undefined) row.prontuario = client.prontuario || [];
  if (client.anamnese !== undefined) row.anamnese = client.anamnese || {};
  if (client.historico !== undefined) row.historico = client.historico || [];
  return row;
}

// ─── CRUD ───────────────────────────────────────────────────────────────────

// Lista clientes. O PostgREST do Supabase tem hard cap de 1000 rows por
// request — esse helper itera em páginas até carregar todos (ou bater o
// `limit` solicitado). Pra ~20k clientes são ~20 requests rápidas em paralelo.
export async function listClients({ unitId, search, limit = 100000 } = {}) {
  const max = Math.min(Number(limit) || 100000, 200000);
  const filter = unitId ? `&unit_id=eq.${encodeURIComponent(unitId)}` : '';
  const base = `${SUPABASE_URL}/rest/v1/clients?select=*&order=name.asc${filter}`;

  // REGRA LINEAR (15/07/2026): antes, uma página que falhasse virava []
  // silencioso — MIL clientes sumiam da tela sem nenhum erro. Agora qualquer
  // página que falhe LANÇA, e o DataContext (que já tem retry) mantém o
  // estado anterior em vez de sobrescrever com lista capenga.
  let rows = await restFetchAll(base, { maxRows: max });

  if (search && search.trim()) {
    const q = search.trim().toLowerCase();
    rows = rows.filter(r =>
      (r.name || '').toLowerCase().includes(q)
      || (r.phone || '').includes(q)
      || (r.cpf || '').includes(q)
      || (r.email || '').toLowerCase().includes(q)
    );
  }
  return rows.map(toApp);
}

export async function getClient(id) {
  const r = await fetch(`${SUPABASE_URL}/rest/v1/clients?id=eq.${id}&select=*&limit=1`, { headers: restHeaders() });
  if (!r.ok) return null;
  const rows = await r.json();
  return rows?.[0] ? toApp(rows[0]) : null;
}

// Upsert: cria (POST) se id estiver vazio OU se id não for UUID (id legado
// tipo 'cli-1234567890' não bate com clients.id uuid no Supabase — Postgres
// rejeita com 22P02). Pra qualquer id UUID válido, faz PATCH.
const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const isUUID = (id) => typeof id === 'string' && UUID_RE.test(id.trim());

export async function upsertClient(client) {
  const row = { ...toDb(client), updated_at: new Date().toISOString() };
  if (client.id && isUUID(client.id)) {
    const r = await fetch(`${SUPABASE_URL}/rest/v1/clients?id=eq.${client.id}`, {
      method: 'PATCH',
      headers: restHeaders({ Prefer: 'return=representation' }),
      body: JSON.stringify(row),
    });
    if (!r.ok) throw new Error(`Falha ao atualizar cliente: ${await r.text()}`);
    const arr = await r.json();
    // PATCH retorna [] se nada foi atualizado (id inexistente). Trata como insert.
    if (arr && arr.length > 0) return toApp(arr[0]);
  }
  // POST sem id — Supabase gera UUID novo via default gen_random_uuid().
  const { id: _ignored, ...rowNoId } = row;
  const r = await fetch(`${SUPABASE_URL}/rest/v1/clients`, {
    method: 'POST',
    headers: restHeaders({ Prefer: 'return=representation' }),
    body: JSON.stringify(rowNoId),
  });
  if (!r.ok) throw new Error(`Falha ao criar cliente: ${await r.text()}`);
  return toApp((await r.json())[0]);
}

export async function deleteClient(id) {
  const r = await fetch(`${SUPABASE_URL}/rest/v1/clients?id=eq.${id}`, {
    method: 'DELETE', headers: restHeaders(),
  });
  if (!r.ok) throw new Error(`Falha ao excluir cliente: ${await r.text()}`);
  return true;
}

// Merge: junta dois clientes em um. Mantém o primeiro, deleta o segundo.
// Usado pelo MergeClientes — passa dois IDs, e os dados do "source"
// são fundidos com o "target" (preferindo target onde houver conflito).
export async function mergeClients(targetId, sourceId) {
  const [target, source] = await Promise.all([getClient(targetId), getClient(sourceId)]);
  if (!target || !source) throw new Error('Cliente não encontrado');
  // Fusão: prefere o target, mas completa campos vazios com o source.
  const merged = { ...target };
  for (const key of ['phone', 'email', 'cpf', 'birthDate', 'gender', 'address', 'notes', 'unitId']) {
    if (!merged[key] && source[key]) merged[key] = source[key];
  }
  // Soma agregados
  merged.totalVisits = (target.totalVisits || 0) + (source.totalVisits || 0);
  merged.totalSpent = (target.totalSpent || 0) + (source.totalSpent || 0);
  if (source.lastVisit && (!target.lastVisit || source.lastVisit > target.lastVisit)) {
    merged.lastVisit = source.lastVisit;
  }
  // Concatena listas (prontuario/historico)
  merged.prontuario = [...(target.prontuario || []), ...(source.prontuario || [])];
  merged.historico = [...(target.historico || []), ...(source.historico || [])];
  // Anamnese: target prevalece sobre source
  merged.anamnese = { ...(source.anamnese || {}), ...(target.anamnese || {}) };
  // Salva e deleta source
  await upsertClient(merged);
  await deleteClient(sourceId);
  return merged;
}
